import React, { useEffect, useState } from 'react';
import { ChevronDown, Play } from 'lucide-react';
import { heroData, portfolioData } from '../data/mock';
import { useModal } from '../App';

const HeroSection = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { openModal } = useModal();

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#04101C]"
    >
      {/* Gradient Background */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#00C4CC]/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#00E08F]/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-[#FFF7AE]/10 rounded-full blur-[80px] animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Floating Podcast Covers */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {portfolioData.slice(0, 4).map((podcast, i) => (
          <div
            key={podcast.id}
            className={`absolute w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden shadow-2xl border border-[#00C4CC]/30 transition-all duration-1000 ${
              isLoaded ? 'opacity-60' : 'opacity-0'
            }`}
            style={{
              top: `${15 + i * 20}%`,
              left: i % 2 === 0 ? '5%' : 'auto',
              right: i % 2 === 1 ? '5%' : 'auto',
              transform: `rotate(${i % 2 === 0 ? -12 : 12}deg)`,
              transitionDelay: `${i * 200}ms`,
              animation: `float ${4 + i}s ease-in-out infinite`,
              animationDelay: `${i * 0.5}s`
            }}
          >
            <img
              src={podcast.image}
              alt={podcast.title}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20">
        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00C4CC]/10 border border-[#00C4CC]/30 mb-8 transition-all duration-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="w-2 h-2 rounded-full bg-[#00E08F] animate-pulse" />
          <span className="text-[#00E08F] text-sm font-medium">Full-Stack Podcast Agency</span>
        </div>

        {/* Headline */}
        <h1
          className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#F5FDFF] leading-tight mb-6 transition-all duration-700 delay-200 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {heroData.headline.split(' ').map((word, i) => (
            <span
              key={i}
              className={word === 'Impact' ? 'bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] bg-clip-text text-transparent' : ''}
            >
              {word}{' '}
            </span>
          ))}
        </h1>

        {/* Subheadline */}
        <p
          className={`text-lg md:text-xl text-[#F5FDFF]/70 max-w-2xl mx-auto mb-10 transition-all duration-700 delay-300 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {heroData.subheadline}
        </p>

        {/* CTAs */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 transition-all duration-700 delay-500 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <button 
            onClick={openModal}
            className="group relative px-8 py-4 rounded-full bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] text-[#04101C] font-bold text-lg hover:shadow-[0_0_40px_rgba(0,224,143,0.5)] transition-all duration-300 hover:scale-105"
          >
            <span className="relative z-10">{heroData.primaryCta}</span>
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] opacity-0 group-hover:opacity-100 blur-xl transition-opacity" />
          </button>

          <a 
            href="#process"
            className="group flex items-center gap-2 px-8 py-4 rounded-full border-2 border-[#00C4CC]/50 text-[#F5FDFF] font-semibold hover:border-[#00E08F] hover:bg-[#00E08F]/10 transition-all duration-300"
          >
            <Play size={20} className="fill-current" />
            {heroData.secondaryCta}
          </a>
        </div>

        {/* Animated Player UI */}
        <div
          className={`relative max-w-md mx-auto transition-all duration-1000 delay-700 ${
            isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}
        >
          <div className="bg-[#0a1a2e]/80 backdrop-blur-xl rounded-2xl p-4 border border-[#00C4CC]/20 shadow-2xl">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00C4CC] to-[#00E08F] flex items-center justify-center">
                <Play size={24} className="text-[#04101C] fill-current ml-1" />
              </div>
              <div className="flex-1">
                <p className="text-[#F5FDFF] font-medium text-sm">Now Playing</p>
                <p className="text-[#00E08F] text-xs">Your Future Podcast</p>
              </div>
              <div className="flex items-end gap-0.5 h-8">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 rounded-full bg-gradient-to-t from-[#00C4CC] to-[#00E08F]"
                    style={{
                      animation: `waveform 0.8s ease-in-out infinite`,
                      animationDelay: `${i * 0.1}s`,
                      height: '40%'
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="mt-3 h-1 bg-[#1a2a3e] rounded-full overflow-hidden">
              <div className="h-full w-1/3 bg-gradient-to-r from-[#00C4CC] to-[#00E08F] rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ChevronDown size={32} className="text-[#00E08F]/60" />
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(var(--rotation, -12deg)); }
          50% { transform: translateY(-20px) rotate(var(--rotation, -12deg)); }
        }
        @keyframes waveform {
          0%, 100% { height: 40%; }
          50% { height: 100%; }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
